import { LockClosedIcon } from "@radix-ui/react-icons"
import { useEffect } from "react"
import { Button } from "@components/common/button"
import axios from "axios"
import { useNavigate, useSearchParams } from "react-router-dom"
import { setCookie } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { toast } from "@hooks/use-toast"
import {
  INVITATIONS_ACCEPT_PAGE_ROUTE,
  LOGIN_COMPLETE_PAGE_ROUTE,
  LOGIN_PAGE_ROUTE,
} from "@pma/utils/page-routes"
import {
  GOOGLE_OAUTH_SERVER,
  GOOGLE_OAUTH_CONTINUE_ENDPOINT,
  GOOGLE_OAUTH_INIT_ENDPOINT,
} from "@pma/utils/api-routes"
import useTranslator from "@hooks/use-translator"
import { DictType } from "@pma/types"
import { useAuth } from "@pma/stores/auth-store"

const GoogleSignIn = () => {
  const { translate } = useTranslator()
  const navigate = useNavigate()
  const { authConfig } = useAuth()

  const invitationKey = authConfig?.invitationKey

  const isLocalHost = process.env.NODE_ENV === "development"

  const redirectUri = isLocalHost
    ? `http://localhost:3000${LOGIN_PAGE_ROUTE}`
    : `https://rog-tech.io${LOGIN_PAGE_ROUTE}`

  const [searchParams] = useSearchParams()
  const googleCode = searchParams.get("code")
  const accessDenied = searchParams.get("access_denied")

  const signIn = () => {
    axios
      .get(`${GOOGLE_OAUTH_SERVER}${GOOGLE_OAUTH_INIT_ENDPOINT}?redirect_uri=${redirectUri}`)
      .then((res) => {
        window.location.href = res.data.authUrl
      })
      .catch((error: DictType) => {
        const errorMessage = error?.response?.data?.message

        toast({
          title: translate("Error"),
          description: errorMessage || translate("Unable to login at this time"),
          variant: "error",
        })
      })
  }

  const finalizeGoogleAuth = (code: string) => {
    const googleContinueUrl = `${GOOGLE_OAUTH_SERVER}${GOOGLE_OAUTH_CONTINUE_ENDPOINT}?code=${code}&redirect_uri=${redirectUri}`

    axios
      .get(googleContinueUrl)
      .then(async (res) => {
        const { token } = res.data

        setCookie(null, ACCESS_TOKEN, token, {
          maxAge: 24 * 60 * 60,
          sameSite: "strict",
          path: "/",
        })

        toast({
          title: translate("Success"),
          description: translate("Login successful"),
          variant: "success",
        })

        if (invitationKey) navigate(INVITATIONS_ACCEPT_PAGE_ROUTE)
        else navigate(LOGIN_COMPLETE_PAGE_ROUTE)
      })
      .catch((error: any) => {
        const errorMessage = error?.response?.data.message
        toast({
          title: translate("Error validating login"),
          description: translate(errorMessage || "Unable to login at this time"),
          variant: "error",
        })
      })
  }

  useEffect(() => {
    if (googleCode) {
      finalizeGoogleAuth(googleCode)
    }
    if (accessDenied) {
      toast({
        title: translate("Access Denied"),
        description: translate("Unable to login at this time"),
        variant: "error",
      })
    }
  }, [])

  return (
    <Button
      variant='outline'
      className='w-full rounded-full capitalize text-black'
      type='button'
      onClick={signIn}
      leftSection={<LockClosedIcon className='h-4 w-4' />}
    >
      {translate("google")}
    </Button>
  )
}

export default GoogleSignIn
